import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import Link from '@atlaskit/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { fg } from '@confluence/feature-gating';

import { PAGE_INHERITED_RESTRICTIONS_SUPPORT_LINK } from './archivingConstants';
import { i18nCommonStrings } from './commonStrings';

type RestrictionsWarningMessageProps = {
	title: MessageDescriptor;
	message: MessageDescriptor;
	dataTestid?: string;
	// All warning messages have a 16px padding by default, but this prop allows
	// for the padding to be removed in cases where it isn't needed. An example of
	// such a case would be when the warning message is the first element in the
	// dialog. This prop defaults to true.
	hasMessagePadding?: boolean;
};

export const RestrictionsWarningMessage: React.FC<RestrictionsWarningMessageProps> = ({
	title,
	message,
	dataTestid = 'archive-pages-restrictions-warning-message',
	hasMessagePadding = true,
}) => {
	const intl = useIntl();

	const viewRestrictionSupportLink = {
		a: (chunks: React.ReactFragment) =>
			fg('dst-a11y__replace-anchor-with-link__admin-experien') ? (
				<Link
					aria-label={intl.formatMessage(
						i18nCommonStrings.archiveInheritedRestrictionsInfoLinkAriaDescription,
					)}
					target="_blank"
					href={PAGE_INHERITED_RESTRICTIONS_SUPPORT_LINK}
				>
					{chunks}
				</Link>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
				<a
					aria-label={intl.formatMessage(
						i18nCommonStrings.archiveInheritedRestrictionsInfoLinkAriaDescription,
					)}
					target="_blank"
					href={PAGE_INHERITED_RESTRICTIONS_SUPPORT_LINK}
				>
					{chunks}
				</a>
			),
	};

	return (
		<Box
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			xcss={xcss({ paddingBlock: hasMessagePadding ? 'space.200' : 'space.0' })}
			testId={dataTestid.concat('-container')}
		>
			<SectionMessage appearance="warning" title={intl.formatMessage(title)}>
				{/* Note we can't put a test ID on a <FormattedMessage> component so we'll put it here instead */}
				<Text testId={dataTestid} as="p">
					<FormattedMessage
						{...message}
						values={{
							lineBreak: <br />,
							...viewRestrictionSupportLink,
						}}
					/>
				</Text>
			</SectionMessage>
		</Box>
	);
};
